import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Input,
} from '@mui/joy';
import {
  X as CloseIcon,
  MagnifyingGlass as SearchIcon,
} from '@phosphor-icons/react';

interface Shortcut {
  keys: string[];
  description: string;
  category: string;
}

const SHORTCUTS: Shortcut[] = [
  // Navigation
  { keys: ['?'], description: 'Show keyboard shortcuts', category: 'Navigation' },
  { keys: ['Ctrl', 'K'], description: 'Quick search', category: 'Navigation' },
  { keys: ['Esc'], description: 'Close modal/dialog', category: 'Navigation' },
  
  // Editing
  { keys: ['Ctrl', 'S'], description: 'Save changes', category: 'Editing' },
  { keys: ['Ctrl', 'Z'], description: 'Undo', category: 'Editing' },
  { keys: ['Ctrl', 'Y'], description: 'Redo', category: 'Editing' },
  { keys: ['Ctrl', 'Shift', 'Z'], description: 'Redo (Mac)', category: 'Editing' },
  { keys: ['Ctrl', 'D'], description: 'Duplicate section', category: 'Editing' },
  { keys: ['Delete'], description: 'Delete selected', category: 'Editing' },
  
  // Publishing
  { keys: ['Ctrl', 'P'], description: 'Preview page', category: 'Publishing' },
  { keys: ['Ctrl', 'Shift', 'P'], description: 'Publish page', category: 'Publishing' },
  
  // Content
  { keys: ['Ctrl', 'N'], description: 'New page', category: 'Content' },
  { keys: ['Ctrl', 'G'], description: 'Generate AI content', category: 'Content' },
  { keys: ['Ctrl', 'I'], description: 'Insert image', category: 'Content' },
  
  // View
  { keys: ['Ctrl', '+'], description: 'Zoom in', category: 'View' },
  { keys: ['Ctrl', '-'], description: 'Zoom out', category: 'View' },
  { keys: ['Ctrl', '0'], description: 'Reset zoom', category: 'View' },
  { keys: ['F11'], description: 'Toggle fullscreen', category: 'View' },
];

interface KeyboardShortcutsProps {
  open: boolean;
  onClose: () => void;
}

export const KeyboardShortcuts: React.FC<KeyboardShortcutsProps> = ({
  open,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  const formatKey = (key: string) => {
    if (isMac) {
      return key
        .replace('Ctrl', '⌘')
        .replace('Alt', '⌥')
        .replace('Shift', '⇧')
        .replace('Delete', '⌫');
    }
    return key;
  };

  const filteredShortcuts = SHORTCUTS.filter(shortcut =>
    shortcut.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shortcut.keys.some(key => key.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const groupedShortcuts = filteredShortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, Shortcut[]>);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ maxWidth: 600 }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography level="h4">Keyboard Shortcuts</Typography>
            <IconButton size="sm" variant="plain" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Input
            placeholder="Search shortcuts..."
            startDecorator={<SearchIcon />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
          />

          {Object.entries(groupedShortcuts).map(([category, shortcuts]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography level="title-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                {category}
              </Typography>
              
              {shortcuts.map((shortcut, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1,
                    px: 2,
                    borderRadius: 'sm',
                    '&:hover': {
                      bgcolor: 'background.level1',
                    },
                  }}
                >
                  <Typography level="body-sm">
                    {shortcut.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {shortcut.keys.map((key, keyIndex) => (
                      <React.Fragment key={keyIndex}>
                        {keyIndex > 0 && (
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            +
                          </Typography>
                        )}
                        <Chip
                          size="sm"
                          variant="outlined"
                          sx={{
                            fontFamily: 'monospace',
                            minWidth: 'auto',
                          }}
                        >
                          {formatKey(key)}
                        </Chip>
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              ))}
              
              {category !== Object.keys(groupedShortcuts)[Object.keys(groupedShortcuts).length - 1] && (
                <Divider sx={{ mt: 2 }} />
              )}
            </Box>
          ))}

          {filteredShortcuts.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                No shortcuts found
              </Typography>
            </Box>
          )}
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};

// Hook to handle keyboard shortcuts
export const useKeyboardShortcuts = (shortcuts: Record<string, () => void>) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;
      const alt = e.altKey;

      // Build shortcut string
      let shortcut = '';
      if (ctrl) shortcut += 'ctrl+';
      if (shift) shortcut += 'shift+';
      if (alt) shortcut += 'alt+';
      shortcut += key;

      // Check if shortcut exists and call it
      if (shortcuts[shortcut]) {
        e.preventDefault();
        shortcuts[shortcut]();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);
};
